import { Identifier } from "@modules/shared/enums/common-enum";
import { ErrorEntity } from "@modules/shared/entities/errors/error.entity";
import { useContext } from "react";
import { AuthenticationEntity } from "../entities/auth.entity";
import { AuthenticationService } from "../services/auth.service";
import { AuthContext } from "../contexts/auth-plus.context";
import { requestPermission } from "@modules/shared/firebase/firebase";
import FcmTokenService from "@modules/services/fcm-token.service";
import { message } from "antd";

export const useAuthentication = () => {
  const authenticationService = new AuthenticationService();
  const { auth, setAuth, logout: contextLogout } = useContext(AuthContext);

  const login = async (
    identifier: string,
    password: string,
    loginType: Identifier,
  ): Promise<AuthenticationEntity | ErrorEntity> => {
    try {
      const responseData = await authenticationService.login(
        identifier,
        password,
        loginType,
      );

      if (responseData instanceof ErrorEntity) {
        setAuth(null);
        return responseData;
      }

      const refreshToken = responseData.getRefreshToken();
      const accessToken = responseData.getAccessToken();

      // Lưu cả access token và refresh token
      sessionStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("refresh_token", refreshToken);
      sessionStorage.setItem("access_token", accessToken);
      localStorage.setItem("access_token", accessToken);

      setAuth(responseData);

      try {
        const fcmToken = await requestPermission();
        if (fcmToken) {
          await FcmTokenService(
            { token: fcmToken },
            responseData,
            setAuth,
            contextLogout,
          );
        }
      } catch (err) {
        message.error("Can't save FCM token after login !");
        throw err;
      }

      return responseData;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = async (): Promise<boolean> => {
    try {
      const accessToken = auth?.getAccessToken() ?? "";
      const isLogoutSuccess = await authenticationService.logout(accessToken);

      if (isLogoutSuccess) {
        sessionStorage.removeItem("refresh_token");
        localStorage.removeItem("refresh_token");
        sessionStorage.removeItem("access_token");
        localStorage.removeItem("access_token");
        setAuth(null);
        contextLogout();
        return true;
      }

      return false;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  };

  const signup = async (
    name: string,
    phoneNumber: string,
    email: string,
    password: string,
    secretKey: string,
  ): Promise<AuthenticationEntity | ErrorEntity> => {
    try {
      const responseData = await authenticationService.signUp(
        name,
        phoneNumber,
        email,
        password,
        secretKey,
      );

      if (responseData instanceof ErrorEntity) {
        setAuth(null);
        return responseData;
      }

      const refreshToken = responseData.getRefreshToken();
      const accessToken = responseData.getAccessToken();

      sessionStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("refresh_token", refreshToken);
      sessionStorage.setItem("access_token", accessToken);
      localStorage.setItem("access_token", accessToken);

      setAuth(responseData);
      return responseData;
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  return { login, logout, signup };
};
